import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { chunk, first, uniq } from 'lodash-es';
import { EMPTY, Observable, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { TopicActions } from './topic.actions';
import { ColumnType } from '../../features/market-comparison/models/column-type.enum';
import { Column, ColumnId, ColumnItemValue } from '../../features/market-comparison/models/column.model';
import { Topic } from '../../features/topic/models/topic.model';
import { MarketComparisonService } from '../../features/market-comparison/services/market-comparison.service';
import { TopicService } from '../../features/topic/services/topic.service';
import {
  FAIL_NOTIFICATION_CONFIGURATION,
  SUCCESS_NOTIFICATION_CONFIGURATION,
} from '../../features/shared/constants/notification-configurations';
import { arrayToFixedValueRecord } from '../../features/shared/utils/array-to-record';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DatapointRuleService } from '../../features/unified-datapoint/services/datapoint-rule.service';
import { AppActions } from '../app/app.actions';
import { ExtractionRuleListActions } from '../extraction-rule-list/extraction-rule-list.actions';
import { ExtractionRuleListSelectors } from '../extraction-rule-list/extraction-rule-list.selectors';
import { RootState } from '../root-state.model';

@Injectable()
export class TopicEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly marketComparisonService = inject(MarketComparisonService);
  private readonly topicService = inject(TopicService);
  private readonly datapointRuleService = inject(DatapointRuleService);

  private readonly COLUMNS_IN_CHUNK_SIZE = 4;
  private readonly UNGROUPABLE_COLUMNS = [ColumnType.ParagraphExtractionRule, ColumnType.Field];

  startLoadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.LoadTopic),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const allColumns = action.columns || store.topic.columns;

        const groupableColumns: Column<any>[] = allColumns.filter((c) => !this.UNGROUPABLE_COLUMNS.includes(c.type));
        const ungroupableColumns: Column<any>[] = allColumns.filter((c) => this.UNGROUPABLE_COLUMNS.includes(c.type));

        const chunks: Column[][] = [
          ...ungroupableColumns.map((c) => [c]),
          ...chunk(groupableColumns, this.COLUMNS_IN_CHUNK_SIZE),
        ];

        return of(...chunks).pipe(
          map((columns) =>
            TopicActions.LoadTopicValues({
              columns,
              transactionIds: action.transactionIds,
              loadingColumns: arrayToFixedValueRecord(columns, true, 'id'),
              force: action.force,
            })
          )
        );
      })
    )
  );

  loadSingleByHashEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.LoadTopicValueByHash),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.extractionRuleList.rules),
      mergeMap(([action, store]) => {
        const datapointRules = store.extractionRuleList.rules.filter((r) => r.ruleHash === action.datapointHash);
        const datapointRuleIds = new Set(datapointRules.map((r) => r.id));
        const columns = store.topic.columns.filter((c) => datapointRuleIds.has(c.value as string));

        return this.marketComparisonService
          .getColumnItemValueRecordByMatrix(columns, [action.transactionId], true, true)
          .pipe(
            withLatestFrom(this.store),
            map(([result, actualStore]) =>
              TopicActions.LoadTopicValuesSuccess({
                list: columns.reduce(
                  (acc, column) => {
                    acc[column.id] = {
                      ...acc[column.id],
                      ...(first(result) || {}),
                    };
                    return acc;
                  },
                  { ...actualStore.topic.list }
                ),
                finishedColumns: {},
              })
            )
          );
      })
    )
  );

  loadValuesByHashesEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.LoadTopicValuesByHashes),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.extractionRuleList.rules),
      mergeMap(([action, store]) => {
        const hashes = new Set(action.changes.map((change) => change.datapointHash));
        const datapointRuleIds = new Set(
          store.extractionRuleList.rules.filter((r) => hashes.has(r.ruleHash)).map((r) => r.id)
        );
        const columns = store.topic.columns.filter((c) => datapointRuleIds.has(c.value as string));
        const transactionIds = uniq(action.changes.map((change) => change.transactionId));

        if (!columns.length || !transactionIds.length) {
          return EMPTY;
        }

        return this.marketComparisonService
          .getColumnItemValueRecordByMatrix(columns, transactionIds, true, true)
          .pipe(
            withLatestFrom(this.store),
            map(([result, actualStore]) =>
              TopicActions.LoadTopicValuesSuccess({
                list: columns.reduce(
                  (acc, column, index) => {
                    acc[column.id] = {
                      ...acc[column.id],
                      ...(result[index] || {}),
                    };
                    return acc;
                  },
                  { ...actualStore.topic.list }
                ),
                finishedColumns: {},
              })
            )
          );
      })
    )
  );

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.LoadTopicValues),
      withLatestFrom(this.store),
      mergeMap(([action, store]) =>
        this.marketComparisonService
          .getColumnItemValueRecordByMatrix(action.columns, action.transactionIds, true, action.force)
          .pipe(
            map((list) =>
              TopicActions.LoadTopicValuesSuccess({
                list: action.columns.reduce<Record<ColumnId, ColumnItemValue>>((acc, column, index) => {
                  acc[column.id] = list[index];
                  return acc;
                }, {}),
                finishedColumns: arrayToFixedValueRecord(action.columns, false, 'id'),
              })
            )
          )
      )
    )
  );

  SaveTopicDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.SaveTopicDetails),
      withLatestFrom(this.store),
      map(([action, store]) => ({
        topic: store.topicList.topics.find((t) => t.id === action.data.id),
        action,
      })),
      filter(({ topic }) => !!topic && topic.mutable),
      switchMap(({ action, topic }) => {
        return this.topicService
          .update(
            {
              ...topic,
              ...action.data,
              name: action.data.displayName,
            },
            true,
            !!topic.canShare,
            !!topic.canPublish
          )
          .pipe(
            map((t) => TopicActions.SaveTopicSuccess({ topic: t, showNotification: true })),
            reportEffectError(TopicActions.SaveTopicFail)
          );
      })
    )
  );

  SaveTopics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.SaveTopic),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const topicId = action.topicId || store.topic.topicId;
        const topic = store.topicList.topics.find((t) => t.id === topicId);
        return { action, store, topic };
      }),
      switchMap(({ action, store, topic }) => {
        const modifiedTopic: Topic = {
          ...(topic || ({} as Topic)),
          columns: action.columns || store.topic.columns,
          sort: store.marketComparison.sort,
          rowHeight: store.topic.rowHeight,
          frequentEntryHightlights: !!store.topic.frequentEntryHightlights,
          name: topic?.name,
          description: topic?.description,
        };

        if (!topic?.mutable) {
          return of(TopicActions.SaveTopicSuccess({ topic: modifiedTopic, showNotification: false }));
        }

        return this.topicService.update(modifiedTopic, true, false, false).pipe(
          map((t) => TopicActions.SaveTopicSuccess({ topic: t, showNotification: false })),
          reportEffectError(TopicActions.SaveTopicFail)
        );
      })
    )
  );

  saveTopicSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.SaveTopicSuccess),
      filter((action) => !!action.showNotification),
      map((action) =>
        AppActions.PushNotification({
          message: `Topic updated`,
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  saveTopicFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.SaveTopicFail),
      map((action) =>
        AppActions.PushNotification({
          message: `Failed to update the topic`,
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  rerunDatapoint$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.ReRunSingleDatapoint),
      withLatestFrom(this.store, this.store.select(ExtractionRuleListSelectors.rulesInRecord)),
      switchMap(([action, store, ruleLookup]) => {
        return this.datapointRuleService
          .runRule(ruleLookup[action.datapointRuleId], [action.transactionId], 'priority')
          .pipe(
            map((t) => TopicActions.ReRunSingleDatapointInitiated(action)),
            reportEffectError(TopicActions.ReRunSingleDatapointFailed)
          );
      })
    )
  );

  rerunDatapointInitiated$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.ReRunSingleDatapointInitiated),
      map((action) => ExtractionRuleListActions.LoadSingleRule({ id: action.datapointRuleId }))
    )
  );
}
